import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Space,
  Spin, message, Table, Modal, Form, Input, InputNumber,
  DatePicker, Select, Tabs, Alert, Popconfirm, Progress,
  Empty, Badge, Tooltip
} from 'antd';
import {
  CalendarOutlined,
  WalletOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
  CoffeeOutlined,
  TagOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import StaffLayout from './StaffLayout';
import api from '../api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const StaffLeave = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [encashments, setEncashments] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [holidayTemplateName, setHolidayTemplateName] = useState('');
  const [activeTab, setActiveTab] = useState('leaves');
  const [leaveStatusFilter, setLeaveStatusFilter] = useState('ALL');

  // Apply Leave Modal state
  const [applyModalVisible, setApplyModalVisible] = useState(false);
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [calculatingRange, setCalculatingRange] = useState(false);
  const [rangeCheckInfo, setRangeCheckInfo] = useState(null);
  const [applyForm] = Form.useForm();

  // Encashment Modal state
  const [encashModalVisible, setEncashModalVisible] = useState(false);
  const [submittingEncash, setSubmittingEncash] = useState(false);
  const [encashForm] = Form.useForm();

  // Load all initial data
  const loadData = async () => {
    setLoading(true);
    try {
      const [catRes, leaveRes, encashRes, holRes] = await Promise.all([
        api.get('/leave/categories').catch(() => ({ data: null })),
        api.get('/leave/me').catch(() => ({ data: null })),
        api.get('/leave/encash/claims/me').catch(() => ({ data: null })),
        api.get('/leave/holidays/my').catch(() => ({ data: null })),
      ]);

      if (catRes.data?.success) {
        setCategories(Array.isArray(catRes.data.categories) ? catRes.data.categories : []);
      }
      if (leaveRes.data?.success) {
        setLeaves(Array.isArray(leaveRes.data.leaves) ? leaveRes.data.leaves : []);
      }
      if (encashRes.data?.success) {
        setEncashments(Array.isArray(encashRes.data.claims) ? encashRes.data.claims : []);
      }
      if (holRes.data?.success) {
        setHolidays(Array.isArray(holRes.data.holidays) ? holRes.data.holidays : []);
        setHolidayTemplateName(holRes.data.templateName || 'Organization Holidays');
      }
    } catch (err) {
      console.error('Error loading leave data:', err);
      message.error('Failed to load leave records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Check working days range for Apply Leave
  const handleDatesChange = async (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      setRangeCheckInfo(null);
      return;
    }
    const startStr = dates[0].format('YYYY-MM-DD');
    const endStr = dates[1].format('YYYY-MM-DD');
    setCalculatingRange(true);
    try {
      const res = await api.get(`/leave/check-range?start=${startStr}&end=${endStr}`);
      if (res.data?.success) {
        setRangeCheckInfo(res.data);
      } else {
        setRangeCheckInfo(null);
      }
    } catch (_) {
      setRangeCheckInfo(null);
    } finally {
      setCalculatingRange(false);
    }
  };

  // Submit Leave Application
  const handleApplyLeave = async (values) => {
    setSubmittingLeave(true);
    try {
      const startDate = values.dates[0].format('YYYY-MM-DD');
      const endDate = values.dates[1].format('YYYY-MM-DD');
      const payload = {
        leaveType: values.categoryKey,
        categoryKey: values.categoryKey,
        startDate,
        endDate,
        reason: values.reason
      };

      const res = await api.post('/leave', payload);
      if (res.data?.success) {
        message.success('Leave applied successfully!');
        setApplyModalVisible(false);
        applyForm.resetFields();
        setRangeCheckInfo(null);
        loadData();
      } else {
        message.error(res.data?.message || 'Failed to apply leave');
      }
    } catch (err) {
      console.error('Apply leave error:', err);
      message.error(err.response?.data?.message || 'Failed to submit leave application');
    } finally {
      setSubmittingLeave(false);
    }
  };

  // Cancel Leave Request (for PENDING leaves)
  const handleCancelLeave = async (id) => {
    try {
      const res = await api.delete(`/leave/${id}`);
      if (res.data?.success) {
        message.success('Leave request cancelled successfully');
        loadData();
      } else {
        message.error(res.data?.message || 'Could not cancel leave request');
      }
    } catch (err) {
      console.error('Cancel leave error:', err);
      message.error(err.response?.data?.message || 'Failed to cancel leave request');
    }
  };

  // Submit Encashment Claim
  const handleClaimEncashment = async (values) => {
    setSubmittingEncash(true);
    try {
      const payload = {
        categoryKey: values.categoryKey,
        days: values.days,
        monthKey: values.monthKey
      };

      const res = await api.post('/leave/encash/claim', payload);
      if (res.data?.success) {
        message.success('Leave encashment claim submitted successfully!');
        setEncashModalVisible(false);
        encashForm.resetFields();
        loadData();
      } else {
        message.error(res.data?.message || 'Failed to submit encashment claim');
      }
    } catch (err) {
      console.error('Encashment error:', err);
      message.error(err.response?.data?.message || 'Failed to submit encashment claim');
    } finally {
      setSubmittingEncash(false);
    }
  };

  // Generate next 6 months for encashment selection
  const payrollMonths = (() => {
    const list = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      list.push({ key: `${yyyy}-${mm}`, label });
    }
    return list;
  })();

  // Filtered leaves
  const filteredLeaves = leaves.filter(l => {
    if (leaveStatusFilter === 'ALL') return true;
    return l.status === leaveStatusFilter;
  });

  return (
    <StaffLayout>
      <div style={{ paddingBottom: 24 }}>
        {/* 1. TOP HEADER & ACTIONS */}
        <Card
          style={{
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            marginBottom: 20,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}
          bodyStyle={{ padding: 24 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <Space align="center" size={14}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#2563eb',
                fontSize: 24
              }}>
                <CoffeeOutlined />
              </div>
              <div>
                <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 700 }}>
                  Leave & Encashment
                </Title>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Apply leaves, view balances, track approval status, claim encashment & browse holidays
                </Text>
              </div>
            </Space>

            <Space size={12} wrap>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  applyForm.resetFields();
                  setRangeCheckInfo(null);
                  setApplyModalVisible(true);
                }}
                style={{
                  borderRadius: 8,
                  background: '#2563eb',
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
                }}
              >
                Apply Leave
              </Button>

              <Button
                icon={<WalletOutlined />}
                onClick={() => {
                  encashForm.resetFields();
                  setEncashModalVisible(true);
                }}
                style={{
                  borderRadius: 8,
                  background: '#10b981',
                  color: '#fff',
                  borderColor: '#10b981',
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
                }}
              >
                Claim Encashment
              </Button>
            </Space>
          </div>
        </Card>

        {/* 2. LIVE LEAVE BALANCES ROW */}
        <div style={{ marginBottom: 20 }}>
          <Title level={5} style={{ marginBottom: 12, color: '#334155', fontWeight: 600 }}>
            <TagOutlined style={{ marginRight: 8, color: '#2563eb' }} />
            My Leave Balances
          </Title>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <Spin tip="Loading balances..." />
            </div>
          ) : categories.length === 0 ? (
            <Alert message="No leave policy assigned currently." type="info" showIcon />
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 16
            }}>
              {categories.map((c, idx) => {
                const remaining = Math.max(0, Number(c.remaining || 0));
                const total = Math.max(0, Number(c.allocated || 0));
                const used = Math.max(0, Number(c.used || 0));
                const percent = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
                const colors = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
                const accentColor = colors[idx % colors.length];

                return (
                  <Card
                    key={c.key || c.id || idx}
                    size="small"
                    style={{
                      borderRadius: 12,
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                      background: '#ffffff'
                    }}
                    bodyStyle={{ padding: 16 }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Text strong style={{ fontSize: 14, color: '#1e293b' }}>
                        {c.name}
                      </Text>
                      <Tag color="blue" style={{ fontSize: 11, borderRadius: 6, margin: 0 }}>
                        {c.key ? String(c.key).toUpperCase() : 'LEAVE'}
                      </Tag>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                      <span style={{ fontSize: 26, fontWeight: 700, color: accentColor, lineHeight: 1 }}>
                        {c.unlimited ? '∞' : remaining}
                      </span>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {c.unlimited ? 'Unlimited' : 'days left'}
                      </Text>
                    </div>

                    {!c.unlimited && (
                      <div>
                        <Progress
                          percent={percent}
                          showInfo={false}
                          strokeColor={accentColor}
                          trailColor="#f1f5f9"
                          size="small"
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginTop: 4 }}>
                          <span>Used: {used}</span>
                          <span>Allocated: {total}</span>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* 3. MAIN CONTENT TABS */}
        <Card
          style={{
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'leaves',
                label: (
                  <span>
                    <HistoryOutlined />
                    Leave Requests & History
                    {leaves.length > 0 && (
                      <Badge count={leaves.length} style={{ marginLeft: 6, backgroundColor: '#2563eb' }} />
                    )}
                  </span>
                ),
                children: (
                  <div>
                    {/* Status Filter Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                      <Space>
                        {['ALL', 'PENDING', 'APPROVED', 'REJECTED'].map(st => (
                          <Button
                            key={st}
                            size="small"
                            type={leaveStatusFilter === st ? 'primary' : 'default'}
                            onClick={() => setLeaveStatusFilter(st)}
                            style={{ borderRadius: 6, fontWeight: 500 }}
                          >
                            {st === 'ALL' ? 'All Leaves' : st.charAt(0) + st.slice(1).toLowerCase()}
                          </Button>
                        ))}
                      </Space>

                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          applyForm.resetFields();
                          setRangeCheckInfo(null);
                          setApplyModalVisible(true);
                        }}
                        style={{ borderRadius: 6 }}
                      >
                        Apply Leave
                      </Button>
                    </div>

                    {filteredLeaves.length === 0 ? (
                      <Empty description="No leave requests found" style={{ padding: '40px 0' }} />
                    ) : (
                      <Table
                        dataSource={filteredLeaves}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        size="middle"
                        columns={[
                          {
                            title: 'Leave Dates',
                            key: 'dates',
                            render: (_, r) => (
                              <div>
                                <Text strong style={{ color: '#0f172a' }}>
                                  {dayjs(r.startDate).format('DD MMM YYYY')}
                                  {r.endDate && r.endDate !== r.startDate ? ` - ${dayjs(r.endDate).format('DD MMM YYYY')}` : ''}
                                </Text>
                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                  {dayjs(r.startDate).format('dddd')}
                                </div>
                              </div>
                            )
                          },
                          {
                            title: 'Category',
                            dataIndex: 'categoryKey',
                            key: 'categoryKey',
                            render: (c, r) => (
                              <Tag color="geekblue" style={{ borderRadius: 6, fontWeight: 500 }}>
                                {String(c || r.leaveType || 'LEAVE').toUpperCase()}
                              </Tag>
                            )
                          },
                          {
                            title: 'Days Breakdown',
                            key: 'days',
                            render: (_, r) => {
                              const paid = Number(r.paidDays ?? r.days ?? 0);
                              const unpaid = Number(r.unpaidDays ?? 0);
                              return (
                                <div>
                                  <Text strong>{paid + unpaid} Days</Text>
                                  <div style={{ fontSize: 11, color: '#64748b' }}>
                                    {paid} paid • {unpaid} unpaid
                                  </div>
                                </div>
                              );
                            }
                          },
                          {
                            title: 'Reason',
                            dataIndex: 'reason',
                            key: 'reason',
                            render: (txt) => txt || <Text type="secondary">--</Text>
                          },
                          {
                            title: 'Applied On',
                            dataIndex: 'createdAt',
                            key: 'createdAt',
                            render: (d) => d ? dayjs(d).format('DD MMM YYYY') : '--'
                          },
                          {
                            title: 'Approval Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (s) => {
                              const conf = {
                                APPROVED: { color: 'green', text: 'Approved', icon: <CheckCircleOutlined /> },
                                PENDING: { color: 'gold', text: 'Pending Approval', icon: <ClockCircleOutlined /> },
                                REJECTED: { color: 'red', text: 'Rejected', icon: <CloseCircleOutlined /> },
                              }[s] || { color: 'default', text: s, icon: null };

                              return (
                                <Tag color={conf.color} icon={conf.icon} style={{ borderRadius: 6, fontWeight: 600 }}>
                                  {conf.text}
                                </Tag>
                              );
                            }
                          },
                          {
                            title: 'Action',
                            key: 'action',
                            render: (_, r) => (
                              r.status === 'PENDING' ? (
                                <Popconfirm
                                  title="Cancel Leave Request"
                                  description="Are you sure you want to cancel this pending leave request?"
                                  onConfirm={() => handleCancelLeave(r.id)}
                                  okText="Yes, Cancel"
                                  cancelText="No"
                                  okButtonProps={{ danger: true }}
                                >
                                  <Button danger size="small" icon={<DeleteOutlined />} style={{ borderRadius: 6 }}>
                                    Cancel
                                  </Button>
                                </Popconfirm>
                              ) : (
                                <Text type="secondary" style={{ fontSize: 12 }}>--</Text>
                              )
                            )
                          }
                        ]}
                      />
                    )}
                  </div>
                )
              },
              {
                key: 'encashment',
                label: (
                  <span>
                    <WalletOutlined />
                    Leave Encashment
                    {encashments.length > 0 && (
                      <Badge count={encashments.length} style={{ marginLeft: 6, backgroundColor: '#10b981' }} />
                    )}
                  </span>
                ),
                children: (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                      <div>
                        <Title level={5} style={{ margin: 0 }}>Encashment Claims</Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Convert accumulated paid leave balance into extra salary payout
                        </Text>
                      </div>

                      <Button
                        type="primary"
                        icon={<WalletOutlined />}
                        onClick={() => {
                          encashForm.resetFields();
                          setEncashModalVisible(true);
                        }}
                        style={{ borderRadius: 6, background: '#10b981', borderColor: '#10b981' }}
                      >
                        Claim Encashment
                      </Button>
                    </div>

                    {encashments.length === 0 ? (
                      <Empty description="No leave encashment claims submitted" style={{ padding: '40px 0' }} />
                    ) : (
                      <Table
                        dataSource={encashments}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        size="middle"
                        columns={[
                          {
                            title: 'Claimed Date',
                            dataIndex: 'createdAt',
                            key: 'createdAt',
                            render: (d) => d ? dayjs(d).format('DD MMM YYYY') : '--'
                          },
                          {
                            title: 'Payroll Month',
                            dataIndex: 'monthKey',
                            key: 'monthKey',
                            render: (m) => <Tag color="blue" style={{ borderRadius: 6, fontWeight: 600 }}>{m}</Tag>
                          },
                          {
                            title: 'Category',
                            dataIndex: 'categoryKey',
                            key: 'categoryKey',
                            render: (c) => <Text strong>{String(c).toUpperCase()}</Text>
                          },
                          {
                            title: 'Days Encashed',
                            dataIndex: 'days',
                            key: 'days',
                            render: (d) => <Text strong style={{ color: '#0f172a' }}>{d} Days</Text>
                          },
                          {
                            title: 'Estimated Amount',
                            dataIndex: 'amount',
                            key: 'amount',
                            render: (a) => a ? <span style={{ color: '#059669', fontWeight: 600 }}>₹{a}</span> : '--'
                          },
                          {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (s) => {
                              const conf = {
                                APPROVED: { color: 'green', text: 'Approved' },
                                PENDING: { color: 'gold', text: 'Pending Approval' },
                                REJECTED: { color: 'red', text: 'Rejected' },
                              }[s] || { color: 'default', text: s };

                              return <Tag color={conf.color} style={{ borderRadius: 6, fontWeight: 600 }}>{conf.text}</Tag>;
                            }
                          },
                          {
                            title: 'Admin Remarks',
                            dataIndex: 'reviewNote',
                            key: 'reviewNote',
                            render: (n) => n || <Text type="secondary">--</Text>
                          }
                        ]}
                      />
                    )}
                  </div>
                )
              },
              {
                key: 'holidays',
                label: (
                  <span>
                    <CalendarOutlined />
                    Holiday Calendar
                    {holidays.length > 0 && (
                      <Badge count={holidays.length} style={{ marginLeft: 6, backgroundColor: '#8b5cf6' }} />
                    )}
                  </span>
                ),
                children: (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <div>
                        <Title level={5} style={{ margin: 0 }}>{holidayTemplateName}</Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Official paid holidays recognized by your company for the current cycle
                        </Text>
                      </div>
                    </div>

                    {holidays.length === 0 ? (
                      <Empty description="No organization holidays found." style={{ padding: '40px 0' }} />
                    ) : (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                        gap: 16
                      }}>
                        {holidays.map((h, idx) => {
                          const isUpcoming = dayjs(h.date).isAfter(dayjs().subtract(1, 'day'));
                          const isPast = dayjs(h.date).isBefore(dayjs().startOf('day'));

                          return (
                            <div
                              key={h.id || idx}
                              style={{
                                padding: '14px 16px',
                                borderRadius: 12,
                                background: isPast ? '#f8fafc' : '#ffffff',
                                border: isUpcoming ? '1px solid #c7d2fe' : '1px solid #e2e8f0',
                                boxShadow: isUpcoming ? '0 2px 8px rgba(99, 102, 241, 0.08)' : '0 1px 3px rgba(0,0,0,0.02)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                opacity: isPast ? 0.7 : 1
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 10,
                                  background: isUpcoming ? '#eff6ff' : '#f1f5f9',
                                  color: isUpcoming ? '#2563eb' : '#64748b',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  lineHeight: 1.1
                                }}>
                                  <span style={{ fontSize: 14, fontWeight: 700 }}>
                                    {dayjs(h.date).format('DD')}
                                  </span>
                                  <span style={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 600 }}>
                                    {dayjs(h.date).format('MMM')}
                                  </span>
                                </div>

                                <div>
                                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                                    {h.name || h.title || 'Official Holiday'}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#64748b' }}>
                                    {dayjs(h.date).format('dddd, YYYY')}
                                  </div>
                                </div>
                              </div>

                              <div>
                                {isUpcoming ? (
                                  <Tag color="purple" style={{ borderRadius: 6, margin: 0, fontWeight: 600 }}>
                                    Upcoming
                                  </Tag>
                                ) : (
                                  <Tag style={{ borderRadius: 6, margin: 0, color: '#94a3b8' }}>
                                    Past
                                  </Tag>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )
              }
            ]}
          />
        </Card>

        {/* 4. MODAL: Apply Leave */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CoffeeOutlined style={{ color: '#2563eb' }} />
              <span>Apply for Leave</span>
            </div>
          }
          open={applyModalVisible}
          onCancel={() => setApplyModalVisible(false)}
          onOk={() => applyForm.submit()}
          confirmLoading={submittingLeave}
          okText="Submit Leave"
          destroyOnClose
        >
          <Form
            form={applyForm}
            layout="vertical"
            onFinish={handleApplyLeave}
          >
            <Form.Item
              name="categoryKey"
              label="Leave Category"
              rules={[{ required: true, message: 'Please select a leave category' }]}
            >
              <Select placeholder="Select leave category">
                {categories.map(c => (
                  <Option key={c.key} value={c.key}>
                    {c.name} ({c.unlimited ? 'Unlimited' : `${Math.max(0, Number(c.remaining || 0))} left`})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="dates"
              label="Leave Date Range (Start & End Date)"
              rules={[{ required: true, message: 'Please select start and end dates' }]}
            >
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                onChange={handleDatesChange}
              />
            </Form.Item>

            {calculatingRange && (
              <div style={{ margin: '8px 0', color: '#2563eb', fontSize: 13 }}>
                <Spin size="small" style={{ marginRight: 6 }} /> Calculating working days & policy rules...
              </div>
            )}

            {rangeCheckInfo && (
              <Alert
                style={{ marginBottom: 16 }}
                type={rangeCheckInfo.sandwichPolicy ? 'warning' : 'info'}
                showIcon
                message={
                  <div>
                    <Text strong>Calculated Duration: </Text>
                    <span>{rangeCheckInfo.totalDays || rangeCheckInfo.workingDays} Day(s) </span>
                    {rangeCheckInfo.weeklyOffsCount > 0 && (
                      <span style={{ color: '#64748b' }}>({rangeCheckInfo.weeklyOffsCount} Weekly Offs excluded)</span>
                    )}
                    {rangeCheckInfo.holidaysCount > 0 && (
                      <span style={{ color: '#64748b' }}> ({rangeCheckInfo.holidaysCount} Holidays excluded)</span>
                    )}
                  </div>
                }
                description={
                  rangeCheckInfo.sandwichPolicy
                    ? 'Sandwich policy applies: Adjacent weekend/holiday will be counted under leave.'
                    : undefined
                }
              />
            )}

            <Form.Item
              name="reason"
              label="Reason for Leave"
              rules={[{ required: true, message: 'Please specify the reason' }]}
            >
              <TextArea rows={3} placeholder="Provide a brief explanation for your leave request" />
            </Form.Item>
          </Form>
        </Modal>

        {/* 5. MODAL: Claim Leave Encashment */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <WalletOutlined style={{ color: '#10b981' }} />
              <span>Claim Leave Encashment</span>
            </div>
          }
          open={encashModalVisible}
          onCancel={() => setEncashModalVisible(false)}
          onOk={() => encashForm.submit()}
          confirmLoading={submittingEncash}
          okText="Submit Claim"
          destroyOnClose
        >
          <Form
            form={encashForm}
            layout="vertical"
            onFinish={handleClaimEncashment}
          >
            <Form.Item
              name="categoryKey"
              label="Leave Category"
              rules={[{ required: true, message: 'Please select category to encash' }]}
            >
              <Select placeholder="Select category with positive balance">
                {categories.filter(c => !c.unlimited && Number(c.remaining || 0) > 0).map(c => (
                  <Option key={c.key} value={c.key}>
                    {c.name} ({c.remaining} days available)
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="days"
              label="Number of Days to Encash"
              rules={[
                { required: true, message: 'Please enter days' },
                {
                  validator: (_, val) => {
                    if (val && Number(val) <= 0) {
                      return Promise.reject(new Error('Days must be greater than 0'));
                    }
                    const selectedCat = encashForm.getFieldValue('categoryKey');
                    const catObj = categories.find(c => c.key === selectedCat);
                    if (catObj && Number(val) > Number(catObj.remaining || 0)) {
                      return Promise.reject(new Error(`Only ${catObj.remaining} days available for encashment`));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={0.5} step={0.5} placeholder="e.g. 2 or 5" />
            </Form.Item>

            <Form.Item
              name="monthKey"
              label="Payroll Month for Payout"
              rules={[{ required: true, message: 'Please select payroll month' }]}
            >
              <Select placeholder="Select payout month">
                {payrollMonths.map(m => (
                  <Option key={m.key} value={m.key}>
                    {m.label} ({m.key})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Alert
              type="info"
              showIcon
              message="Payout Information"
              description="Encashed leaves will be paid along with your monthly salary for the selected payroll month following admin verification."
            />
          </Form>
        </Modal>
      </div>
    </StaffLayout>
  );
};

export default StaffLeave;